import React from 'react';
import { Linking, Pressable, ScrollView, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { GlassButton } from '../../components/ui/GlassButton';
import { APP_NAME, SUPPORT_EMAIL } from '../../config/branding';
import { navigateToLegal } from '../../core/navigation/navigationRef';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { MarketingBackBar } from './MarketingBackBar';
import {
  LEGAL_UPDATED,
  PRIVACY_BLOCKS,
  TERMS_BLOCKS,
  type LegalBlock,
} from './legalCopy';

type LegalKind = 'privacy' | 'terms' | 'support';

const SUPPORT_SECTIONS: { title: string; body: string }[] = [
  {
    title: 'Account and sign-in',
    body: 'Sign in with Google, Sign in with Apple, or your phone. The same profile works on iPhone, Android, and the web. If you cannot get in, write from the email or number on the account.',
  },
  {
    title: 'Imports',
    body: 'Upload a statement PDF or a screenshot of a card, bank, or brokerage app. We never ask for a bank password. If a file fails, attach it when you write — redact amounts if you prefer.',
  },
  {
    title: 'Billing',
    body: 'Pro is a monthly subscription. Manage or cancel from Settings after you sign in. For a charge you do not recognize, include the date and last four digits of the card.',
  },
  {
    title: 'Household',
    body: 'Invite a partner by email or text. Free is two people. If an invite never arrives, check spam, then email us the address you sent it to.',
  },
  {
    title: 'Delete account',
    body: 'In the app, open the account menu, tap Settings, then Delete account at the bottom. You can also sign in at moneylensai.ca/app/delete-account. Complete all three confirmations (acknowledgements, type your email or phone, type DELETE). We soft-delete: sign-in stops, records stay stored internally, and we never permanently erase associated data. Cancel any Google Play, App Store, or Stripe subscription separately.',
  },
];

function titleFor(kind: LegalKind): string {
  if (kind === 'privacy') return 'Privacy Policy';
  if (kind === 'terms') return 'Terms of Use';
  return 'Support';
}

function barTitleFor(kind: LegalKind): string {
  if (kind === 'privacy') return 'Privacy';
  if (kind === 'terms') return 'Terms';
  return 'Support';
}

async function openHref(url: string): Promise<void> {
  const canOpen = await Linking.canOpenURL(url);
  if (canOpen) {
    await Linking.openURL(url);
  }
}

function LegalBlocks({ blocks }: { blocks: LegalBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'h') {
          return (
            <Text key={index} style={styles.sectionTitle}>
              {block.text}
            </Text>
          );
        }
        if (block.type === 'highlight') {
          return (
            <View key={index} style={styles.highlight}>
              <Text style={styles.highlightText}>{block.text}</Text>
            </View>
          );
        }
        if (block.type === 'ul' || block.type === 'ol' || block.type === 'steps') {
          const numbered = block.type === 'ol' || block.type === 'steps';
          return (
            <View key={index} style={block.type === 'steps' ? styles.steps : styles.list}>
              {block.items.map((item, itemIndex) => (
                <View key={item} style={styles.listRow}>
                  <Text style={styles.bullet}>{numbered ? `${itemIndex + 1}.` : '•'}</Text>
                  <Text style={block.type === 'steps' ? styles.stepText : styles.body}>{item}</Text>
                </View>
              ))}
            </View>
          );
        }
        if (block.type === 'cta') {
          return (
            <GlassButton
              key={index}
              variant="primary"
              accessibilityLabel={block.text}
              onPress={() => void openHref(block.href)}
            >
              {block.text}
            </GlassButton>
          );
        }
        return (
          <Text key={index} style={styles.body}>
            {block.text}
          </Text>
        );
      })}
    </>
  );
}

export function LegalScreen({ kind }: { kind: LegalKind }) {
  const insets = useSafeAreaInsets();
  const title = titleFor(kind);
  const blocks =
    kind === 'privacy' ? PRIVACY_BLOCKS : kind === 'terms' ? TERMS_BLOCKS : null;

  return (
    <View style={styles.root}>
      <MarketingBackBar title={barTitleFor(kind)} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: Math.max(insets.bottom, spacing.xl) + spacing.xl },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.kicker}>{APP_NAME}</Text>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.updated}>
          Updated {LEGAL_UPDATED}
        </Text>
        {kind === 'support' ? (
          <>
            <Text style={styles.body}>
              Email {SUPPORT_EMAIL} and include the email or phone you sign in with, plus a short
              description of what went wrong. We typically reply within two business days.
            </Text>
            <GlassButton
              variant="primary"
              accessibilityLabel={`Email ${SUPPORT_EMAIL}`}
              onPress={() =>
                void openHref(`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`${APP_NAME} support`)}`)
              }
            >
              Email support
            </GlassButton>
            {SUPPORT_SECTIONS.map((section) => (
              <View key={section.title} style={styles.section}>
                <Text style={styles.sectionTitle}>{section.title}</Text>
                <Text style={styles.body}>{section.body}</Text>
              </View>
            ))}
          </>
        ) : (
          blocks && <LegalBlocks blocks={blocks} />
        )}
        <View style={styles.footerLinks}>
          <Pressable
            accessibilityRole="link"
            onPress={() => navigateToLegal('Privacy')}
            style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
          >
            <Text style={styles.footerLinkText}>Privacy</Text>
          </Pressable>
          <Pressable
            accessibilityRole="link"
            onPress={() => navigateToLegal('Terms')}
            style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
          >
            <Text style={styles.footerLinkText}>Terms</Text>
          </Pressable>
          <Pressable
            accessibilityRole="link"
            onPress={() => navigateToLegal('Support')}
            style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
          >
            <Text style={styles.footerLinkText}>Support</Text>
          </Pressable>
          <Pressable
            accessibilityRole="link"
            onPress={() => navigateToLegal('DeleteAccount')}
            style={({ pressed }) => [styles.footerLink, pressed && styles.pressed]}
          >
            <Text style={styles.footerLinkText}>Delete account</Text>
          </Pressable>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.lg,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
    gap: spacing.md,
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  title: {
    ...typography.title,
    color: colors.text,
  },
  updated: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 24,
    flex: 1,
  },
  highlight: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    borderLeftWidth: 3,
    borderLeftColor: colors.warning,
    borderRadius: radii.sm,
    padding: spacing.md,
  },
  highlightText: {
    ...typography.body,
    color: colors.text,
    lineHeight: 24,
    fontWeight: '600',
  },
  list: {
    gap: spacing.sm,
  },
  listRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  bullet: {
    ...typography.body,
    color: colors.accent,
    lineHeight: 24,
    minWidth: 22,
    fontWeight: '600',
  },
  stepText: {
    ...typography.body,
    color: colors.text,
    lineHeight: 24,
    fontWeight: '500',
    flex: 1,
  },
  steps: {
    gap: spacing.sm,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.28)',
    borderRadius: radii.md,
    padding: spacing.md,
  },
  section: {
    gap: spacing.xs,
  },
  sectionTitle: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
    marginTop: spacing.sm,
  },
  footerLinks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    marginTop: spacing.lg,
  },
  footerLink: {
    minHeight: 44,
    justifyContent: 'center',
    cursor: 'pointer',
  } as ViewStyle,
  footerLinkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
  },
  pressed: {
    opacity: 0.85,
  },
});
